import React, { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from "recharts";
import CountUp from "react-countup";
import { Link } from "react-router-dom";
import Navbarpro from "./sidebar";
import { api } from "../../../config";

function Dashboard() {
  const [data, setData] = useState({
    overview: { totalVendors: 0, activeVendors: 0, pendingVendors: 0, totalCustomers: 0, totalOrders: 0, monthlyOrders: 0, totalBookings: 0 },
    categoryDistribution: [],
    monthlyRevenue: [],
    recentOrders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Try new admin analytics endpoint first
        let analyticsData;
        try {
          const res = await api.get('/admin/analytics');
          analyticsData = res.data;
        } catch {
          // Fallback to legacy endpoints
          const [vendorCount, pendingCount, recentOrders] = await Promise.all([
            api.get('/vendor/count').catch(() => ({ data: { count: 0, count1: 0 } })),
            api.get('/vendor/countofpendingrequest').catch(() => ({ data: { valueofrequest: 0 } })),
            api.get('/dashboard/recent').catch(() => ({ data: [] }))
          ]);

          analyticsData = {
            overview: {
              totalVendors: vendorCount.data?.count || 0,
              activeVendors: vendorCount.data?.count || 0,
              pendingVendors: pendingCount.data?.valueofrequest || 0,
              totalCustomers: 0,
              totalOrders: recentOrders.data?.length || 0,
              monthlyOrders: 0,
              totalBookings: 0
            },
            categoryDistribution: [
              { _id: 'Technical', count: Math.floor((vendorCount.data?.count || 0) * 0.4) },
              { _id: 'Non-Technical', count: Math.floor((vendorCount.data?.count || 0) * 0.35) },
              { _id: 'Products', count: Math.floor((vendorCount.data?.count || 0) * 0.25) }
            ],
            monthlyRevenue: [],
            recentOrders: recentOrders.data || []
          };
        }

        setData(analyticsData);
      } catch (err) {
        console.error("Dashboard error:", err);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const COLORS = ['#FF7600', '#00C853', '#00D4FF', '#7C4DFF'];

  const statsCards = [
    { label: "Total Vendors", value: data.overview.totalVendors, icon: "bi-shop-window", color: "var(--primary)", link: "/vendors" },
    { label: "Active Vendors", value: data.overview.activeVendors, icon: "bi-check-circle", color: "var(--accent-green)" },
    { label: "Pending Approvals", value: data.overview.pendingVendors, icon: "bi-hourglass-split", color: "var(--accent-yellow)", link: "/pending" },
    { label: "Total Customers", value: data.overview.totalCustomers, icon: "bi-people", color: "var(--accent-cyan)", link: "/customers" },
    { label: "Total Orders", value: data.overview.totalOrders, icon: "bi-bag", color: "var(--accent-purple)", link: "/orders" },
    { label: "This Month", value: data.overview.monthlyOrders, icon: "bi-calendar-check", color: "var(--accent-green)" },
  ];

  const quickActions = [
    { label: "Add Vendor", icon: "bi-plus-circle", link: "/vendor", variant: "outline-primary" },
    { label: "Pending", icon: "bi-clock", link: "/pending", variant: "outline-warning", badge: data.overview.pendingVendors },
    { label: "All Vendors", icon: "bi-people", link: "/vendors", variant: "outline-info" },
    { label: "Orders", icon: "bi-bag", link: "/orders", variant: "outline-success" },
    { label: "Customers", icon: "bi-person-lines-fill", link: "/customers", variant: "outline-cyan" },
    { label: "Notifications", icon: "bi-bell", link: "/notification", variant: "outline-secondary" },
  ];

  if (loading) {
    return (
      <div className="loading-state">
        <Navbarpro />
        <div className="text-center" style={{ marginTop: '20vh' }}>
          <div className="spinner-border" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="mt-3">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbarpro />

      <div className="container-fluid py-4 px-4">
        {/* Page Header */}
        <div className="page-header d-flex justify-content-between align-items-center mb-4 fade-in">
          <div>
            <h4 className="mb-1">
              <i className="bi bi-speedometer2 me-2" style={{ color: 'var(--primary)' }}></i>
              Dashboard
            </h4>
            <p className="text-secondary mb-0">Welcome back! Here's your platform overview.</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => window.location.reload()}>
              <i className="bi bi-arrow-clockwise me-1"></i> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning fade-in">
            <i className="bi bi-exclamation-triangle me-2"></i>{error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="row g-3 mb-4">
          {statsCards.map((stat, idx) => (
            <div className="col-xl-2 col-lg-4 col-md-6" key={idx}>
              <div
                className="stat-card fade-in"
                style={{ animationDelay: `${idx * 0.1}s`, cursor: stat.link ? 'pointer' : 'default' }}
                onClick={() => stat.link && (window.location.href = stat.link)}
              >
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="icon" style={{ background: `${stat.color}20`, color: stat.color }}>
                    <i className={`bi ${stat.icon}`}></i>
                  </div>
                  {stat.link && (
                    <i className="bi bi-arrow-up-right" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}></i>
                  )}
                </div>
                <div className="value">
                  <CountUp end={stat.value} duration={2} separator="," />
                </div>
                <div className="label">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="row g-4 mb-4">
          {/* Revenue Trend */}
          <div className="col-lg-8">
            <div className="chart-container fade-in">
              <h6 className="mb-4">
                <i className="bi bi-graph-up me-2" style={{ color: 'var(--primary)' }}></i>
                Monthly Revenue Trend
              </h6>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.monthlyRevenue.length > 0 ? data.monthlyRevenue : [
                  { _id: 'Jan', revenue: 42000 }, { _id: 'Feb', revenue: 38000 },
                  { _id: 'Mar', revenue: 55000 }, { _id: 'Apr', revenue: 48000 },
                  { _id: 'May', revenue: 62000 }, { _id: 'Jun', revenue: 58000 }
                ]}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF7600" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#FF7600" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis dataKey="_id" stroke="var(--text-secondary)" fontSize={12} />
                  <YAxis stroke="var(--text-secondary)" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px' }}
                    labelStyle={{ color: 'var(--text-primary)' }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#FF7600" fill="url(#colorRevenue)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Distribution */}
          <div className="col-lg-4">
            <div className="chart-container fade-in h-100">
              <h6 className="mb-4">
                <i className="bi bi-pie-chart me-2" style={{ color: 'var(--accent-cyan)' }}></i>
                Vendor Distribution
              </h6>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={data.categoryDistribution.map(c => ({ name: c._id, value: c.count }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {data.categoryDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="d-flex justify-content-center gap-3 mt-2">
                {data.categoryDistribution.map((item, index) => (
                  <div key={index} className="text-center">
                    <div
                      className="rounded-circle mx-auto mb-1"
                      style={{ width: 12, height: 12, backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <small style={{ color: 'var(--text-secondary)' }}>{item._id}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Recent Orders */}
        <div className="row g-4">
          {/* Quick Actions */}
          <div className="col-lg-4">
            <div className="chart-container fade-in h-100">
              <h6 className="mb-4">
                <i className="bi bi-lightning me-2" style={{ color: 'var(--accent-yellow)' }}></i>
                Quick Actions
              </h6>
              <div className="quick-actions-grid">
                {quickActions.map((action, idx) => (
                  <Link
                    key={idx}
                    to={action.link}
                    className={`btn btn-${action.variant} position-relative`}
                  >
                    <i className={`bi ${action.icon} fs-4`}></i>
                    <span>{action.label}</span>
                    {action.badge > 0 && (
                      <span className="position-absolute top-0 end-0 translate-middle badge rounded-pill bg-danger">
                        {action.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="col-lg-8">
            <div className="chart-container fade-in">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="mb-0">
                  <i className="bi bi-bag me-2" style={{ color: 'var(--accent-green)' }}></i>
                  Recent Orders
                </h6>
                <Link to="/orders" className="btn btn-link text-primary p-0">
                  View All <i className="bi bi-arrow-right"></i>
                </Link>
              </div>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentOrders.length > 0 ? (
                      data.recentOrders.slice(0, 5).map((order, idx) => (
                        <tr key={idx}>
                          <td><code>#{order._id?.slice(-6).toUpperCase()}</code></td>
                          <td>{order.customerName || 'Customer'}</td>
                          <td>₹{order.totalPrice || 0}</td>
                          <td>
                            <span className={`badge bg-${order.orderStatus === 'Delivered' ? 'success' :
                                order.orderStatus === 'Cancelled' ? 'danger' :
                                  order.orderStatus === 'Shipped' ? 'info' : 'warning'
                              }`}>
                              {order.orderStatus || 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-secondary">
                          No recent orders
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
